import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLogStream, LogEntry, ConnectionStatus } from '../../hooks/useLogStream';
import { apiService } from '../../services/api';
import { MigrationLogsResponse } from '../../types/api';
import Logger from '../../utils/logger';
import { useTranslation } from 'react-i18next';
import LogsToolbar, { DEFAULT_TOOLBAR_STATE, LogsToolbarState, TimePreset } from './LogsToolbar';
import { parseLogsSearch, buildFilterParam } from '../../utils/logsSearchParser';
import { formatPodName } from '../../utils/formatPodName';

interface DeploymentLogsSectionProps {
  deploymentId: string;
  migrationEnabled?: boolean;
}

type LogTab = 'application' | 'migration';

const DeploymentLogsSection: React.FC<DeploymentLogsSectionProps> = ({
  deploymentId,
  migrationEnabled = false,
}) => {
  const { t } = (useTranslation as any)('deployments');
  const [autoScroll, setAutoScroll] = useState(true);
  const [activeTab, setActiveTab] = useState<LogTab>('application');
  const logsContainerRef = useRef<HTMLDivElement>(null);

  // Application logs (SSE streaming)
  const {
    logs,
    connectionStatus,
    connect,
    disconnect,
    clearLogs,
    isStreaming,
    errorMessage,
  } = useLogStream(deploymentId);

  // Migration logs state
  const [migrationLogs, setMigrationLogs] = useState<MigrationLogsResponse | null>(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationError, setMigrationError] = useState<string | null>(null);

  // Toolbar state (search, time, filters, live tail)
  const [toolbarState, setToolbarState] = useState<LogsToolbarState>(DEFAULT_TOOLBAR_STATE);

  // Historic logs (fetched via GET /logs when liveTail is OFF)
  const [historicLogs, setHistoricLogs] = useState<string>('');
  const [historicLoading, setHistoricLoading] = useState(false);
  const [historicError, setHistoricError] = useState<string | null>(null);

  // Pods list (populated once on mount for the toolbar dropdown)
  const [pods, setPods] = useState<Array<{ name: string }>>([]);

  // Download state (spinner + disable during blob fetch)
  const [downloading, setDownloading] = useState(false);

  // Compute `since` from time preset (custom = ISO datetime → seconds diff)
  const sinceString = useMemo(
    () => timePresetToSince(toolbarState.timePreset, toolbarState.customSince),
    [toolbarState.timePreset, toolbarState.customSince]
  );
  const sinceSeconds = useMemo(() => sinceToSeconds(sinceString), [sinceString]);

  // Compose backend filter from parsed search + selected levels
  const parsed = useMemo(() => parseLogsSearch(toolbarState.searchInput), [toolbarState.searchInput]);
  const backendFilter = useMemo(
    () => buildFilterParam(parsed, toolbarState.levels) || undefined,
    [parsed, toolbarState.levels]
  );
  // `instance=` / `component=` sont les alias user-friendly de `pod=` / `container=`
  // pour ne pas exposer le vocabulaire K8s dans la syntaxe de recherche.
  const effectivePod = toolbarState.pod || parsed.labels.pod || parsed.labels.instance || undefined;
  const effectiveContainer = toolbarState.container || parsed.labels.container || parsed.labels.component || undefined;

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs, historicLogs, migrationLogs, autoScroll]);

  // Disconnect stream on unmount to release the SSE connection
  useEffect(() => {
    return () => {
      if (isStreaming) {
        disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load pods once on mount (best-effort — fallback silencieux si endpoint down)
  useEffect(() => {
    let cancelled = false;
    apiService.getApplicationPods(deploymentId)
      .then((r) => { if (!cancelled) setPods(r.pods || []); })
      .catch((e) => { Logger.warn('Failed to load pods list:', e); });
    return () => { cancelled = true; };
  }, [deploymentId]);

  const fetchHistoric = useCallback(async () => {
    setHistoricLoading(true);
    setHistoricError(null);
    try {
      const r = await apiService.getApplicationLogs(deploymentId, {
        container: effectiveContainer,
        since: sinceString,
        tailLines: 1000,
      });
      setHistoricLogs(r.logs || '');
    } catch (e: any) {
      Logger.error('Failed to fetch application logs:', e);
      setHistoricError(e.response?.data?.detail || 'Erreur lors de la récupération des logs');
    } finally {
      setHistoricLoading(false);
    }
  }, [deploymentId, sinceString, effectiveContainer]);

  const handleApply = useCallback(async () => {
    if (toolbarState.liveTail) {
      // Reset streaming state before opening a new connection with fresh params
      clearLogs();
      await connect({
        pod: effectivePod,
        container: effectiveContainer,
        filter: backendFilter,
        since: sinceSeconds,
        tail: 200,
      });
    } else {
      if (isStreaming) disconnect();
      await fetchHistoric();
    }
  }, [toolbarState.liveTail, effectivePod, effectiveContainer, backendFilter, sinceSeconds, clearLogs, connect, disconnect, isStreaming, fetchHistoric]);

  // Auto-apply on mount with default toolbar state (single shot on application tab)
  const hasAutoAppliedRef = useRef(false);
  useEffect(() => {
    if (activeTab === 'application' && !hasAutoAppliedRef.current) {
      hasAutoAppliedRef.current = true;
      handleApply();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await apiService.downloadApplicationLogs(deploymentId, sinceString);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `logs-${deploymentId}-${sinceString}-${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      Logger.error('Failed to download logs', err);
    } finally {
      setDownloading(false);
    }
  }, [deploymentId, sinceString]);

  const handleClear = () => {
    clearLogs();
    setHistoricLogs('');
  };

  // Client-side text filter for historic (backend /logs n'accepte pas filter)
  const historicFilteredLines = useMemo(() => {
    if (!historicLogs) return [] as string[];
    const lines = historicLogs.split('\n');
    if (!parsed.terms.length && !parsed.excludes.length && !toolbarState.levels.length) return lines;
    return lines.filter((line) => {
      const low = line.toLowerCase();
      for (const term of parsed.terms) if (!low.includes(term.toLowerCase())) return false;
      for (const x of parsed.excludes) if (low.includes(x.toLowerCase())) return false;
      if (toolbarState.levels.length) {
        const has = toolbarState.levels.some((l) => low.includes(l.toLowerCase()));
        if (!has) return false;
      }
      return true;
    });
  }, [historicLogs, parsed, toolbarState.levels]);

  const fetchMigrationLogs = async () => {
    setMigrationLoading(true);
    setMigrationError(null);
    try {
      const response = await apiService.getMigrationLogs(deploymentId, '24h', 500);
      setMigrationLogs(response);
    } catch (error: any) {
      Logger.error('Failed to fetch migration logs:', error);
      setMigrationError(error.response?.data?.detail || 'Erreur lors de la récupération des logs de migration');
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleTabChange = (tab: LogTab) => {
    setActiveTab(tab);
    if (tab === 'migration' && !migrationLogs && !migrationLoading) {
      fetchMigrationLogs();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const getStatusDot = (status: ConnectionStatus) => {
    const baseClasses = 'inline-block w-2 h-2 rounded-full';
    switch (status) {
      case 'connected':
        return <span className={`${baseClasses} bg-green-500`} title={t('logs.connected') as string} />;
      case 'connecting':
        return <span className={`${baseClasses} bg-yellow-500 animate-pulse`} title={t('logs.connectionInProgress') as string} />;
      case 'error':
        return <span className={`${baseClasses} bg-red-500`} title={t('logs.error') as string} />;
      case 'disconnected':
      default:
        return <span className={`${baseClasses} bg-gray-400`} title={t('logs.disconnected') as string} />;
    }
  };

  const getStatusText = (status: ConnectionStatus) => {
    switch (status) {
      case 'connected':
        return t('logs.connected') as string;
      case 'connecting':
        return t('logs.connecting') as string;
      case 'error':
        return t('logs.error') as string;
      case 'disconnected':
      default:
        return t('logs.disconnected') as string;
    }
  };

  const getMigrationStatusBadge = () => {
    if (!migrationLogs) return null;

    const status = migrationLogs.job_status?.toLowerCase();
    if (status === 'healthy' || status === 'succeeded') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/12 text-green-400">
          Succès
        </span>
      );
    } else if (status === 'degraded' || status === 'failed') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-500/12 text-red-400">
          Échec
        </span>
      );
    } else if (status === 'progressing' || status === 'running') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/12 text-yellow-400">
          En cours
        </span>
      );
    }
    return null;
  };

  return (
    <div className="bg-surface-2 shadow rounded-lg overflow-hidden">
      {/* Tabs */}
      <div className="border-b border-border-1">
        <nav className="flex -mb-px">
          <button
            onClick={() => handleTabChange('application')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'application'
                ? 'border-accent-500 text-accent-400'
                : 'border-transparent text-text-tertiary hover:text-text-secondary hover:border-border-1'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
              </svg>
              <span>Application</span>
              {activeTab === 'application' && (
                <div className="flex items-center space-x-1">
                  {getStatusDot(connectionStatus)}
                  <span className="text-xs">{getStatusText(connectionStatus)}</span>
                </div>
              )}
            </div>
          </button>
          {migrationEnabled && (
            <button
              onClick={() => handleTabChange('migration')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'migration'
                  ? 'border-accent-500 text-accent-400'
                  : 'border-transparent text-text-tertiary hover:text-text-secondary hover:border-border-1'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
                <span>Migration</span>
                {getMigrationStatusBadge()}
              </div>
            </button>
          )}
        </nav>
      </div>

      {/* Application Logs Tab */}
      {activeTab === 'application' && (
        <>
          {/* Toolbar (search + time + filters + live tail) */}
          <LogsToolbar
            state={toolbarState}
            onChange={setToolbarState}
            onApply={handleApply}
            onDownload={handleDownload}
            downloading={downloading}
            pods={pods}
            loading={historicLoading || connectionStatus === 'connecting'}
          />

          {/* Secondary controls (status dot + clear + autoscroll + count) */}
          <div className="px-4 py-2 bg-surface-0 border-b border-border-1 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {toolbarState.liveTail && (
                <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
                  {getStatusDot(connectionStatus)}
                  <span>{getStatusText(connectionStatus)}</span>
                </div>
              )}
              <button
                onClick={handleClear}
                disabled={logs.length === 0 && !historicLogs}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-text-secondary bg-surface-3 border border-border-1 hover:bg-surface-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('logs.clear')}
              </button>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-xs text-text-tertiary">
                {toolbarState.liveTail
                  ? t('logs.lines', { count: logs.length, plural: logs.length !== 1 ? 's' : '' })
                  : t('logs.lines', { count: historicFilteredLines.length, plural: historicFilteredLines.length !== 1 ? 's' : '' })}
              </span>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-9 h-5 bg-surface-4 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent-600"></div>
                <span className="ml-2 text-xs text-text-tertiary">{t('logs.autoScroll')}</span>
              </label>
            </div>
          </div>

          {/* Error message */}
          {(errorMessage || historicError) && (
            <div className="px-4 py-2 bg-red-500/12 border-b border-red-500/20">
              <p className="text-sm text-red-400 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {historicError || errorMessage}
              </p>
            </div>
          )}

          {/* Logs terminal */}
          <div
            ref={logsContainerRef}
            className="bg-gray-900 p-4 font-mono text-xs h-96 overflow-y-auto logs-terminal-scroll"
          >
            {toolbarState.liveTail ? (
              logs.length === 0 ? (
                <div className="text-gray-500 text-center py-8">
                  {connectionStatus === 'connected' ? (
                    <span>{t('logs.waitingForLogs')}</span>
                  ) : (
                    <span>{t('logs.clickApply')}</span>
                  )}
                </div>
              ) : (
                logs.map((log: LogEntry) => (
                  <div key={log.id} className="py-0.5 hover:bg-gray-800/50">
                    <span className="text-gray-500">[{formatTimestamp(log.timestamp)}]</span>
                    {log.pod && log.pod !== 'unknown' && (
                      <span className="text-accent-400 ml-1" title={log.pod}>[{formatPodName(log.pod)}]</span>
                    )}
                    <span className="text-green-400 ml-2">{log.content}</span>
                  </div>
                ))
              )
            ) : (
              historicLoading ? (
                <div className="text-gray-500 text-center py-8">
                  <svg className="animate-spin mx-auto h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <p className="mt-2">{t('logs.loading')}</p>
                </div>
              ) : historicFilteredLines.length === 0 ? (
                <div className="text-gray-500 text-center py-8">
                  {historicLogs ? t('logs.noMatch') : t('logs.clickApply')}
                </div>
              ) : (
                historicFilteredLines.map((line, i) => (
                  <div key={i} className="py-0.5 hover:bg-gray-800/50">
                    <span className="text-green-400">{line}</span>
                  </div>
                ))
              )
            )}
          </div>
        </>
      )}

      {/* Migration Logs Tab */}
      {activeTab === 'migration' && (
        <>
          {/* Controls bar */}
          <div className="px-4 py-3 bg-surface-0 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              {/* Refresh button */}
              <button
                onClick={fetchMigrationLogs}
                disabled={migrationLoading}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 disabled:bg-accent-400 disabled:cursor-not-allowed transition-colors"
              >
                {migrationLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Chargement...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Rafraîchir
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center space-x-4">
              {/* Job name */}
              {migrationLogs?.migration_job_name && (
                <span className="text-xs text-text-tertiary font-mono truncate max-w-xs">
                  {migrationLogs.migration_job_name}
                </span>
              )}
              {/* Lines count */}
              {migrationLogs?.lines_count !== undefined && (
                <span className="text-xs text-text-tertiary">
                  {migrationLogs.lines_count} ligne{migrationLogs.lines_count !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Error message */}
          {migrationError && (
            <div className="px-4 py-2 bg-red-500/12 border-b border-red-500/20">
              <p className="text-sm text-red-400 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {migrationError}
              </p>
            </div>
          )}

          {/* Migration not enabled message */}
          {migrationLogs && !migrationLogs.migration_enabled && (
            <div className="px-4 py-3 bg-yellow-500/12 border-b border-yellow-500/20">
              <p className="text-sm text-yellow-400 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {migrationLogs.message || 'La migration n\'est pas activée pour cette application.'}
              </p>
            </div>
          )}

          {/* Logs terminal */}
          <div
            ref={logsContainerRef}
            className="bg-gray-900 p-4 font-mono text-xs h-96 overflow-y-auto logs-terminal-scroll"
          >
            {migrationLoading ? (
              <div className="text-gray-500 text-center py-8">
                <svg className="animate-spin mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <p className="mt-2">{t('logs.loadingMigrationLogs')}</p>
              </div>
            ) : !migrationLogs?.logs ? (
              <div className="text-gray-500 text-center py-8">
                <span>{t('logs.noMigrationLogs')}</span>
              </div>
            ) : (
              migrationLogs.logs.split('\n').map((line, index) => (
                <div key={index} className="py-0.5 hover:bg-gray-800/50">
                  <span className="text-green-400">{line}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Convertit un TimePreset (+ éventuel datetime-local custom) en string `since`
// que le backend accepte (ex. "15m", "1h", "300s").
function timePresetToSince(preset: TimePreset, customSince: string): string {
  if (preset !== 'custom') return preset;
  if (!customSince) return '15m';
  const startMs = new Date(customSince).getTime();
  if (isNaN(startMs)) return '15m';
  const diffS = Math.max(60, Math.floor((Date.now() - startMs) / 1000));
  return `${diffS}s`;
}

// Convertit un string `since` (5m, 1h, 300s, 2d) en secondes (pour SSE stream
// qui attend un int en secondes, contrairement à /logs qui prend la string).
function sinceToSeconds(since: string): number {
  const m = /^(\d+)([smhd])$/.exec(since);
  if (!m) return 900; // défaut 15m
  const n = parseInt(m[1], 10);
  const mult: Record<string, number> = { s: 1, m: 60, h: 3600, d: 86400 };
  return n * (mult[m[2]] || 60);
}

export default DeploymentLogsSection;
